<div class="app-api-keys">
  <div class="toolbar">
    <h3 class="title">App API keys</h3>
    <button
      *ngIf="authService.hasPermission(['create'], 'AppApiKeyController')"
      pButton
      type="button"
      label="New key"
      icon="pi pi-plus"
      class="p-button-sm"
      (click)="openCreate()"
    ></button>
  </div>
  <p class="hint">
    Secrets are shown only once when you create or rotate a key. Store them securely.
    <button
      type="button"
      class="usage-info p-button p-button-text p-button-rounded p-button-sm"
      aria-label="How to use this API key"
      (click)="usagePanel.toggle($event)"
    >
      <i class="pi pi-info-circle"></i>
    </button>
  </p>

  <div *ngIf="loading" class="loading">
    <i class="pi pi-spin pi-spinner"></i>
    <span>Loading…</span>
  </div>

  <p-table
    *ngIf="!loading"
    [value]="keys"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
    [filterDelay]="300"
    styleClass="p-datatable-sm app-api-keys-table"
    [sortField]="'id'"
    [sortOrder]="-1"
  >
    <ng-template pTemplate="header">
      <tr>
        <th pSortableColumn="id">
          Id
          <p-sortIcon field="id"></p-sortIcon>
          <p-columnFilter
            type="numeric"
            field="id"
            display="menu"
            [showMatchModes]="true"
            [showOperator]="false"
          ></p-columnFilter>
        </th>
        <th pSortableColumn="name">
          Name
          <p-sortIcon field="name"></p-sortIcon>
          <p-columnFilter
            type="text"
            field="name"
            display="menu"
            [showMatchModes]="true"
            [showOperator]="false"
            [matchModeOptions]="textMatchModeOptions"
          ></p-columnFilter>
        </th>
        <th pSortableColumn="isActive">
          Active
          <p-sortIcon field="isActive"></p-sortIcon>
          <p-columnFilter
            type="boolean"
            field="isActive"
            display="menu"
          ></p-columnFilter>
        </th>
        <th pSortableColumn="_permCount">
          Permissions
          <p-sortIcon field="_permCount"></p-sortIcon>
          <p-columnFilter
            type="numeric"
            field="_permCount"
            display="menu"
            [showMatchModes]="true"
            [showOperator]="false"
          ></p-columnFilter>
        </th>
        <th pSortableColumn="expiresAt">
          Expires
          <p-sortIcon field="expiresAt"></p-sortIcon>
          <p-columnFilter
            type="date"
            field="expiresAt"
            display="menu"
            [showMatchModes]="true"
            [showOperator]="false"
          ></p-columnFilter>
        </th>
        <th pSortableColumn="lastUsedAt">
          Last used
          <p-sortIcon field="lastUsedAt"></p-sortIcon>
          <p-columnFilter
            type="date"
            field="lastUsedAt"
            display="menu"
            [showMatchModes]="true"
            [showOperator]="false"
          ></p-columnFilter>
        </th>
        <th pSortableColumn="createdAt">
          Created
          <p-sortIcon field="createdAt"></p-sortIcon>
          <p-columnFilter
            type="date"
            field="createdAt"
            display="menu"
            [showMatchModes]="true"
            [showOperator]="false"
          ></p-columnFilter>
        </th>
        <th style="width: 12rem">Actions</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-row>
      <tr>
        <td>{{ row.id }}</td>
        <td>{{ row.name }}</td>
        <td>
          <p-tag [severity]="row.isActive ? 'success' : 'danger'" [value]="row.isActive ? 'Yes' : 'No'"></p-tag>
        </td>
        <td class="perm-cell">
          <span *ngIf="permissionCount(row) === 0" class="perm-empty">—</span>
          <button
            *ngIf="permissionCount(row) > 0"
            type="button"
            pButton
            class="p-button-text p-button-sm perm-open-btn"
            icon="pi pi-list"
            [label]="
              permissionCount(row) +
              ' permission' +
              (permissionCount(row) === 1 ? '' : 's')
            "
            (click)="openPermissionsModal(row)"
            pTooltip="View all permissions"
            tooltipPosition="top"
          ></button>
        </td>
        <td>{{ row.expiresAt ? (row.expiresAt | date: 'medium') : '—' }}</td>
        <td>{{ row.lastUsedAt ? (row.lastUsedAt | date: 'medium') : '—' }}</td>
        <td>{{ row.createdAt ? (row.createdAt | date: 'medium') : '—' }}</td>
        <td>
          <button
            *ngIf="authService.hasPermission(['update'], 'AppApiKeyController')"
            pButton
            icon="pi pi-pencil"
            class="p-button-text p-button-sm p-mr-1"
            pTooltip="Edit"
            (click)="openEdit(row)"
          ></button>
          <button
            *ngIf="authService.hasPermission(['update'], 'AppApiKeyController')"
            pButton
            icon="pi pi-refresh"
            class="p-button-text p-button-sm p-mr-1"
            pTooltip="Rotate key"
            (click)="confirmRotate(row)"
          ></button>
          <button
            *ngIf="authService.hasPermission(['delete'], 'AppApiKeyController')"
            pButton
            icon="pi pi-trash"
            class="p-button-text p-button-danger p-button-sm"
            pTooltip="Delete"
            (click)="confirmDelete(row)"
          ></button>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="8">No API keys yet.</td>
      </tr>
    </ng-template>
  </p-table>

  <!-- Create -->
  <p-dialog
    header="New API key"
    [(visible)]="createVisible"
    [modal]="true"
    [style]="{ width: '38rem' }"
    [dismissableMask]="true"
  >
    <div class="field">
      <label for="create-name">Name *</label>
      <input
        id="create-name"
        type="text"
        pInputText
        class="w-full"
        [(ngModel)]="createForm.name"
      />
    </div>
    <div class="field">
      <label for="create-perms">Permissions (for API key access)</label>
      <p-multiSelect
        inputId="create-perms"
        [options]="permissionSelectOptions"
        [(ngModel)]="createForm.permissions_ref"
        optionLabel="label"
        optionValue="value"
        placeholder="Select permission documents"
        [filter]="true"
        display="chip"
        [showToggleAll]="true"
        [appendTo]="'body'"
        [style]="{ width: '100%' }"
      ></p-multiSelect>
    </div>
    <div class="field">
      <label>Expires (optional)</label>
      <p-calendar
        [(ngModel)]="createForm.expiresAt"
        [showIcon]="true"
        [showTime]="true"
        hourFormat="24"
        [appendTo]="'body'"
        placeholder="No expiry"
      ></p-calendar>
    </div>
    <div class="field row">
      <label for="create-active">Active</label>
      <p-inputSwitch id="create-active" [(ngModel)]="createForm.isActive"></p-inputSwitch>
    </div>
    <ng-template pTemplate="footer">
      <button pButton label="Cancel" class="p-button-text" (click)="createVisible = false"></button>
      <button pButton label="Create" icon="pi pi-check" (click)="submitCreate()"></button>
    </ng-template>
  </p-dialog>

  <!-- Edit -->
  <p-dialog
    header="Edit API key"
    [(visible)]="editVisible"
    [modal]="true"
    [style]="{ width: '38rem' }"
    [dismissableMask]="true"
  >
    <div class="field">
      <label for="edit-name">Name *</label>
      <input
        id="edit-name"
        type="text"
        pInputText
        class="w-full"
        [(ngModel)]="editForm.name"
      />
    </div>
    <div class="field">
      <label for="edit-perms">Permissions (for API key access)</label>
      <p-multiSelect
        inputId="edit-perms"
        [options]="permissionSelectOptions"
        [(ngModel)]="editForm.permissions_ref"
        optionLabel="label"
        optionValue="value"
        placeholder="Select permission documents"
        [filter]="true"
        display="chip"
        [showToggleAll]="true"
        [appendTo]="'body'"
        [style]="{ width: '100%' }"
      ></p-multiSelect>
    </div>
    <div class="field">
      <label>Expires (optional)</label>
      <p-calendar
        [(ngModel)]="editForm.expiresAt"
        [showIcon]="true"
        [showTime]="true"
        hourFormat="24"
        [appendTo]="'body'"
        placeholder="No expiry"
      ></p-calendar>
    </div>
    <div class="field row">
      <label for="edit-active">Active</label>
      <p-inputSwitch id="edit-active" [(ngModel)]="editForm.isActive"></p-inputSwitch>
    </div>
    <ng-template pTemplate="footer">
      <button pButton label="Cancel" class="p-button-text" (click)="editVisible = false"></button>
      <button pButton label="Save" icon="pi pi-check" (click)="submitEdit()"></button>
    </ng-template>
  </p-dialog>

  <!-- Permissions detail (read-only list) -->
  <p-dialog
    [header]="permissionsModalHeader"
    [(visible)]="permissionsModalVisible"
    [modal]="true"
    [style]="{ width: 'min(92vw, 52rem)' }"
    [dismissableMask]="true"
    [breakpoints]="{ '960px': '95vw' }"
  >
    <div class="permissions-modal-scroll">
      <p-table
        [value]="permissionsModalList"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50]"
        [filterDelay]="300"
        [showCurrentPageReport]="true"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} permissions"
        styleClass="p-datatable-sm p-datatable-striped permissions-modal-table"
      >
        <ng-template pTemplate="header">
          <tr>
            <th pSortableColumn="name">
              Name
              <p-sortIcon field="name"></p-sortIcon>
              <p-columnFilter
                type="text"
                field="name"
                display="menu"
                [showMatchModes]="true"
                [showOperator]="false"
                [matchModeOptions]="textMatchModeOptions"
              ></p-columnFilter>
            </th>
            <th pSortableColumn="entity">
              Entity
              <p-sortIcon field="entity"></p-sortIcon>
              <p-columnFilter
                type="text"
                field="entity"
                display="menu"
                [showMatchModes]="true"
                [showOperator]="false"
                [matchModeOptions]="textMatchModeOptions"
              ></p-columnFilter>
            </th>
            <th pSortableColumn="_abilitiesText">
              Abilities
              <p-sortIcon field="_abilitiesText"></p-sortIcon>
              <p-columnFilter
                type="text"
                field="_abilitiesText"
                display="menu"
                [showMatchModes]="true"
                [showOperator]="false"
                [matchModeOptions]="textMatchModeOptions"
              ></p-columnFilter>
            </th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-p>
          <tr>
            <td>{{ p.name || '—' }}</td>
            <td>{{ p.entity || '—' }}</td>
            <td class="abilities-cell">{{ abilitiesDisplay(p.abilities) }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="3">No permissions linked.</td>
          </tr>
        </ng-template>
      </p-table>
    </div>
    <ng-template pTemplate="footer">
      <button
        pButton
        type="button"
        label="Close"
        class="p-button-secondary"
        (click)="permissionsModalVisible = false"
      ></button>
    </ng-template>
  </p-dialog>

  <!-- Secret once -->
  <p-dialog
    [header]="secretTitle"
    [(visible)]="secretVisible"
    [modal]="true"
    [style]="{ width: '36rem' }"
    [dismissableMask]="true"
  >
    <p class="warn">
      Copy this value now. You will not be able to see it again.
      <button
      type="button"
      class="usage-info p-button p-button-text p-button-rounded p-button-sm"
      aria-label="How to use this API key"
      (click)="usagePanel.toggle($event)"
    >
      <i class="pi pi-info-circle"></i>
    </button>
    </p>
    <textarea
      pInputTextarea
      [rows]="3"
      class="w-full secret-field"
      readonly
      [(ngModel)]="secretValue"
    ></textarea>
    <ng-template pTemplate="footer">
      <button pButton label="Copy" icon="pi pi-copy" class="p-button-secondary" (click)="copySecret()"></button>
      <button pButton label="Close" (click)="secretVisible = false"></button>
    </ng-template>
  </p-dialog>

  <p-overlayPanel #usagePanel [style]="{ width: 'min(92vw, 24rem)' }">
    <div class="usage-panel">
      <strong>Using your API key</strong>
      <p>Send it in the <code>x-api-key</code> request header:</p>
      <pre>x-api-key: &lt;your-api-key&gt;</pre>
      <p>Example:</p>
      <pre>curl -H "x-api-key: &lt;your-api-key&gt;" \
  &lt;api-url&gt;/api/...</pre>
    </div>
  </p-overlayPanel>
</div>
